/* eslint-disable max-statements */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';

import { Group, Text, MantineTheme } from '@mantine/core';
import { Upload, Photo, X, Icon as TablerIcon } from 'tabler-icons-react';
import { DropzoneStatus } from '@mantine/dropzone';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { BsCheck2Circle, BsXLg } from 'react-icons/bs';
import Compressor from 'compressorjs';
import { uploadS3 } from '../../Utils/uploadS3';
import { float } from 'aws-sdk/clients/frauddetector';
import { store } from '../../../redux/store';
import { globalEnv } from '../../../config/baseConfig';
import axiosInstance from '../../../config/axiosInstance';

export function getIconColor(status: DropzoneStatus, theme: MantineTheme) {
	return status.accepted
		? theme.colors[theme.primaryColor][theme.colorScheme === 'dark' ? 4 : 6]
		: status.rejected
		? theme.colors.red[theme.colorScheme === 'dark' ? 4 : 6]
		: theme.colorScheme === 'dark'
		? theme.colors.dark[0]
		: theme.colors.gray[7];
}

function ImageUploadIcon({
	status,
	...props
}: React.ComponentProps<TablerIcon> & { status: DropzoneStatus }) {
	if (status.accepted) {
		return <Upload {...props} />;
	}

	if (status.rejected) {
		return <X {...props} />;
	}

	return <Photo {...props} />;
}

enum uploadState {
	UPLOADING = 'uploading',
	UPLOADED = 'uploaded',
	FAILED = 'failed',
	PENDING = 'pending'
}

export interface IFile {
	type: FileType;
	url: string;
	uploadState: uploadState;
}

export enum FileType {
	IMAGE = 'image',
	VIDEO = 'video'
}

export interface IUploadFile {
	type: FileType;
	file: any;
	resizedFile?: any;
	name: string;
	isUploading: boolean;
	isUploaded: boolean;
}

export async function readVideos(
	mediaFiles: IFile[],
	setMediaFiles: (mediaFiles: IFile[]) => void,
	uploadFiles: IUploadFile[],
	setUploadFiles: (uploadFiles: IUploadFile[]) => void,
	addedFiles: File[],
	maxNumberOfImages: number
): Promise<any> {
	if (mediaFiles.length < maxNumberOfImages) {
		let mediaFilesCopy = mediaFiles.slice();
		let uploadFilesCopy = uploadFiles.slice();
		for await (const addedFile of addedFiles) {
			const url = URL.createObjectURL(addedFile);

			console.log('url', url);

			mediaFilesCopy = mediaFilesCopy.concat({
				type: FileType.VIDEO,
				url: url,
				uploadState: uploadState.PENDING
			});

			uploadFilesCopy = uploadFilesCopy.concat({
				type: FileType.VIDEO,
				file: addedFile,
				name: addedFile.name,
				isUploaded: false,
				isUploading: false
			});
		}
		setMediaFiles(mediaFilesCopy);
		setUploadFiles(uploadFilesCopy);
	}
}

export async function readImages(
	mediaFiles: IFile[],
	setMediaFiles: (mediaFiles: IFile[]) => void,
	uploadFiles: IUploadFile[],
	setUploadFiles: (uploadFiles: IUploadFile[]) => void,
	addedFiles: File[],
	maxNumberOfImages: number
): Promise<any> {
	if (mediaFiles.length < maxNumberOfImages) {
		let mediaFilesCopy = mediaFiles.slice();
		let uploadFilesCopy = uploadFiles.slice();
		for await (const addedFile of addedFiles) {
			if (uploadFiles.some((uploadFile) => uploadFile.name === addedFile.name))
				return null;

			let compQuality = 0.9;
			if (addedFile.size > 8000000) compQuality = 0.6;
			else if (addedFile.size > 6000000) compQuality = 0.7;
			else if (addedFile.size > 4000000) compQuality = 0.8;
			// else if (addedFile.size > 3000000) compQuality = 0.9;
			else if (addedFile.size < 1000000) compQuality = 1;

			const lowCompressedImage = await compressImage(
				addedFile,
				compQuality,
				3500,
				3500
			);

			if (lowCompressedImage != null) {
				const url = URL.createObjectURL(lowCompressedImage);

				mediaFilesCopy = mediaFilesCopy.concat({
					type: FileType.IMAGE,
					url: url,
					uploadState: uploadState.PENDING
				});

				const lowCompressedFile = new File(
					[lowCompressedImage],
					addedFile.name
				);

				const highCompressedImage = await compressImage(
					lowCompressedImage,
					0.8,
					400,
					400
				);

				const highCompressedFile = new File(
					[highCompressedImage],
					addedFile.name
				);

				uploadFilesCopy = uploadFilesCopy.concat({
					type: FileType.IMAGE,
					file: lowCompressedFile,
					resizedFile: highCompressedFile,
					name: addedFile.name,
					isUploaded: false,
					isUploading: false
				});
			}
		}
		setMediaFiles(mediaFilesCopy);
		setUploadFiles(uploadFilesCopy);
	}
}

function compressImage(
	addedFile: any,
	quality: float,
	maxWidth?: number,
	maxHeight?: number
): Promise<File | Blob> {
	return new Promise((resolve, reject) => {
		new Compressor(addedFile, {
			quality: quality, // the quality of the image, max is 1,

			maxWidth: maxWidth, // the max width of the output image, defaults to 1920px
			maxHeight: maxHeight, // the max height of the output image, defaults to 1920px
			resize: 'cover', // defaults to true, set false if you do not want to resize the image width and height

			success(result) {
				resolve(result);
			},
			error(e) {
				reject(e);
			}
		});
	});
}

export const dropzoneChildren = (
	mediaFiles: any,
	status: DropzoneStatus,
	theme: MantineTheme,
	files: any[],
	setFiles: (files: any[]) => void,
	setMediaFiles: (images: IFile[]) => void,
	maxNumberOfImages: number
) => (
	<Group position="center" style={{ minHeight: 220 }}>
		{mediaFiles.length > 0 ? (
			<>
				{mediaFiles.map((mediaFile: IFile, index: number) => (
					<div key={index} className="w-3/12 z-50 ">
						<MediaFileToUpload
							index={index}
							mediaFile={mediaFile}
							mediaFiles={mediaFiles}
							setMediaFiles={setMediaFiles}
							files={files}
							setFiles={setFiles}
						/>
					</div>
				))}
				<div className="w-full ">
					<div className="mx-auto text-center">
						<Text size="xl" inline className="dark:text-gray-500">
							Drag images here or click to select files
						</Text>
						<Text size="sm" color="dimmed" inline mt={7}>
							{`Maximum number of files is ${maxNumberOfImages}`}
						</Text>
					</div>
				</div>
			</>
		) : (
			<>
				<ImageUploadIcon
					status={status}
					style={{ color: getIconColor(status, theme) }}
					size={80}
				/>
				<div className="text-center">
					<Text size="xl" inline className="dark:text-gray-500">
						Drag images here or click to select files
					</Text>
					<Text size="sm" color="dimmed" inline mt={7}>
						{`Maximum number of files is ${maxNumberOfImages}`}
					</Text>
				</div>
			</>
		)}
	</Group>
);
export enum uploadStateValues {
	SUCCESS = 'success',
	FAILED = 'failed',
	PARTIAL = 'partial'
}

export const uploadFilesAndGetLocation = async (
	media: IFile[],
	setMedia: (media: IFile[]) => void,
	uploadFiles: IUploadFile[],
	title: string
) => {
	const s3 = new uploadS3();
	const uploadedFiles: {
		type: FileType;
		url: string;
		url_low_res?: string;
		index: number;
	}[] = [];

	let mediaCopy = media.slice();
	const updateImageUploadState = (index: number, state: uploadState) => {
		const updatedImages = mediaCopy.slice();
		updatedImages[index] = {
			...updatedImages[index],
			uploadState: state
		};
		mediaCopy = updatedImages;
		setMedia(updatedImages);
	};

	console.log('uploadFilesqsdqsd');

	const authState: any = store.getState().auth;

	const config = {
		headers: { Authorization: 'Bearer ' + authState.ACCESS_TOKEN }
	};
	const { data } = await axiosInstance().get(
		globalEnv.BASE_URL + `/user/cognito-auth`,
		config
	);

	for await (const [index, uploadFile] of uploadFiles.entries()) {
		const indexOfUploadedFile = uploadFiles.indexOf(uploadFile);
		updateImageUploadState(indexOfUploadedFile, uploadState.UPLOADING);
		console.log('uploading');
		try {
			if (uploadFile.type == FileType.IMAGE) {
				const uploadedFile: any = await s3.uploadImageS3(
					uploadFile.file,
					uploadFile.name,
					title,
					data
				);
				const resizedFile: any = await s3.uploadImageS3(
					uploadFile.resizedFile,
					uploadFile.name,
					title,
					data,
					true
				);

				updateImageUploadState(indexOfUploadedFile, uploadState.UPLOADED);

				uploadedFiles.push({
					type: FileType.IMAGE,
					url: uploadedFile['Location'],
					url_low_res: resizedFile['Location'],
					index: index
				});
			} else if (uploadFile.type == FileType.VIDEO) {
				const uploadedFile: any = await s3.uploadVideoS3(
					uploadFile.file,
					uploadFile.name,
					title,
					data
				);

				updateImageUploadState(indexOfUploadedFile, uploadState.UPLOADED);

				uploadedFiles.push({
					type: FileType.VIDEO,
					url: uploadedFile,
					url_low_res: undefined,
					index: index
				});
			}
		} catch (e) {
			updateImageUploadState(indexOfUploadedFile, uploadState.FAILED);
		}
	}
	return uploadedFiles;
};

type imageProps = {
	index: number;
	mediaFile: IFile;
	mediaFiles: IFile[];
	setMediaFiles: (images: IFile[]) => void;
	files: any[];
	setFiles: (files: any[]) => void;
};
export const MediaFileToUpload = ({
	index,
	mediaFile,
	mediaFiles,
	setMediaFiles,
	files,
	setFiles
}: imageProps) => {
	const removeImage = () => {
		const updatedImages = mediaFiles.slice();
		const updatedFiles = files.slice();
		updatedImages.splice(index, 1);
		updatedFiles.splice(index, 1);

		setMediaFiles(updatedImages);
		setFiles(updatedFiles);
	};

	return (
		<div className="relative p-2 justify-center content-center">
			{mediaFile.type === FileType.IMAGE ? (
				<LazyLoadImage
					key={index}
					alt="gallery"
					className="rounded-lg block object-cover object-center aspect-[16/9]"
					effect="blur"
					src={mediaFile.url}
				/>
			) : (
				mediaFile.type === FileType.VIDEO && (
					<div className="rounded-lg block object-cover object-center aspect-[16/9] pb-4 overflow-hidden">
						<video controls>
							<source src={mediaFile.url} type="video/mp4" />
						</video>
					</div>
				)
			)}

			{mediaFile.uploadState === uploadState.UPLOADED ? (
				<div className="absolute inset-0 mx-2 sm:my-3 md:my-3 lg:my-3 bg-gradient-to-t rounded-lg from-black/75 transition-all duration-500 pl-1">
					<BsCheck2Circle
						size="120"
						className="absolute h-[50%] top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] text-gray-100 dark:text-primary text-center transition-all duration-500 opacity-100"
					/>
				</div>
			) : mediaFile.uploadState === uploadState.FAILED ? (
				<div className="absolute inset-0 mx-2 sm:my-3 md:my-3 lg:my-3 bg-gradient-to-t rounded-lg from-black/75 transition-all duration-500 pl-1">
					<BsXLg
						size="120"
						className="absolute h-[50%] top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] text-gray-100 dark:text-primary text-center transition-all duration-500 opacity-100"
					/>
				</div>
			) : mediaFile.uploadState === uploadState.UPLOADING ? (
				<div className="absolute inset-0 mx-2 sm:my-3 md:my-3 lg:my-3 bg-gradient-to-t rounded-lg from-black/75 transition-all duration-500 pl-1">
					<svg
						role="status"
						className="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-purple-700"
						viewBox="0 0 100 101"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
							fill="currentColor"
						></path>
						<path
							d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
							fill="currentFill"
						></path>
					</svg>
				</div>
			) : (
				<div className="absolute inset-0 mx-2 sm:my-3 md:my-3 lg:my-3 bg-gradient-to-t rounded-lg from-black/75 opacity-0 hover:opacity-100 transition-all duration-500 text-center">
					<div className="group">
						<button
							className="absolute top-1 left-2 btn-error btn-xs btn btn-circle btn-outline shadow-md"
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
								removeImage();
							}}
						>
							<BsXLg />
						</button>
						<span
							className="absolute rounded-md shadow-md text-white bg-gray-900 p-2 min-w-max text-xs
									  scale-0 origin-left group-hover:scale-100
						              font-bold transition-all duration-500 opacity-0 group-hover:opacity-100 top-0 left-10"
						>
							{'Remove ❌'}
						</span>
					</div>
				</div>
			)}
		</div>
	);
};
